/**
 * HustleShopPage.jsx — the dedicated HustleShop storefront.
 *
 * Branded shop that surfaces the 100 curated Amazon affiliate products
 * (and any future affiliate vendors — eBay, Catch, Kogan).
 *
 * Routes that land here:
 *   • /hustleshop
 *   • /store/hustleshop
 *   • /store/amazon  (legacy alias)
 *   • hustleapp://hustleshop (Siri/Google Assistant deep link)
 *
 * Brand assets:
 *   • Logo (light): Local Hustle SHOP LIGHT
 *   • Logo (dark):  Local Hustle SHOP DARK
 *
 * Feb 2026 — wired into the shared `PlazaPagination` component so
 * HustleShop matches HustlePlaza and Tackle: grid + list views,
 * items-per-page selector top-right, numbered pagination bottom for
 * list, load-more for grid.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../lib/api";
import PlazaPagination from "../components/plaza/PlazaPagination";
import MarketplaceViewToggle from "../components/plaza/MarketplaceViewToggle";
import SaveValueChip from "../components/plaza/SaveValueChip";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/pdjt1g2n_Local%20Hustle%20Logo%20-%20SHOP%20LIGHT.png";
const LOGO_DARK  = "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/nqmp2a36_Local%20Hustle%20Logo%20-%20SHOP%20DARK.png";

const CATEGORIES = [
  { id: "",                  label: "All",            emoji: "🛍️" },
  { id: "fishing",           label: "Fishing",        emoji: "🎣" },
  { id: "camping",           label: "Camping",        emoji: "🏕️" },
  { id: "marine",            label: "Marine",         emoji: "⛵" },
  { id: "home-garden",       label: "Home & Garden",  emoji: "🌱" },
  { id: "kitchen",           label: "Kitchen",        emoji: "🍳" },
  { id: "electronics",       label: "Electronics",    emoji: "📱" },
  { id: "sports-fitness",    label: "Sports",         emoji: "🏃" },
  { id: "tools-diy",         label: "Tools",          emoji: "🔧" },
  { id: "lifestyle",         label: "Lifestyle",      emoji: "🎁" },
  { id: "books",             label: "Books",          emoji: "📚" },
];

const GRID_SIZES = [24, 48, 96, 192, 384];
const LIST_SIZES = [25, 50, 100, 250, 500];

export default function HustleShopPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";
  const featured = params.get("featured") === "1";

  // iter256 — Marketplace view toggle (MP-only / Retail / All).
  //   Shown when the host is the master Marketplace shop
  //   (hustlemarketplace.au) OR the caller sets ?marketplace=1. Vendor
  //   shops (mode="retail") never render the toggle.
  const host =
    typeof window !== "undefined"
      ? (window.location.hostname || "").toLowerCase().replace(/^www\./, "")
      : "";
  const isMarketplaceHost =
    host === "hustlemarketplace.au" ||
    host === "app.hustlemarketplace.au" ||
    params.get("marketplace") === "1";
  const mpView = params.get("view") || "all"; // "mp" | "retail" | "all"
  const setMpView = (v) => {
    const np = new URLSearchParams(params);
    if (v && v !== "all") np.set("view", v); else np.delete("view");
    setParams(np);
    setPage(1);
  };

  // View mode + pagination — shared PlazaPagination contract.
  const [view, setView] = useState("grid");           // 'list' | 'grid'
  const [gridSize, setGridSize] = useState(48);
  const [listSize, setListSize] = useState(50);
  const [page, setPage] = useState(1);
  const pageLimit = view === "list" ? listSize : gridSize;
  const totalPages = Math.max(1, Math.ceil((total || 0) / listSize));

  const buildUrl = (skip, limit) => {
    const u = new URL(`${API_BASE}/plaza/amazon-products`, window.location.origin);
    if (category) u.searchParams.set("category", category);
    if (q)        u.searchParams.set("q", q);
    if (featured) u.searchParams.set("featured_only", "true");
    // iter256 — forward the MP-view choice so backend can filter by
    // channels when supported. Backend ignores the param today, so
    // the client-side filter below still guarantees the right slice.
    if (isMarketplaceHost && mpView !== "all") u.searchParams.set("view", mpView);
    u.searchParams.set("skip", String(skip));
    u.searchParams.set("limit", String(limit));
    return u.toString();
  };

  // iter256 — client-side filter fallback (works even when the backend
  // has not been re-deployed with channel-aware querying). Applies
  // only on the Marketplace host / ?marketplace=1.
  const filterByMpView = (list) => {
    if (!isMarketplaceHost || mpView === "all") return list;
    return list.filter((p) => {
      const ch = Array.isArray(p.channels) ? p.channels : [];
      const isMp     = ch.includes("marketplace") || Number(p.mp_price) > 0;
      const isRetail = ch.includes("retail")      || !isMp;
      return mpView === "mp" ? isMp : isRetail;
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      // List uses page/skip (replace), grid starts at page 1 & appends via loadMore.
      const skip = view === "list" ? (page - 1) * listSize : 0;
      const r = await fetch(buildUrl(skip, pageLimit));
      const j = await r.json();
      setProducts(j.items || []);
      setTotal(j.total ?? (j.items || []).length);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await fetch(buildUrl(products.length, gridSize));
      const j = await r.json();
      const seen = new Set(products.map((p) => p.slug));
      const fresh = (j.items || []).filter((p) => !seen.has(p.slug));
      setProducts([...products, ...fresh]);
    } finally {
      setLoadingMore(false);
    }
  };

  // Reset to page 1 on any filter change so we don't sit past the last page.
  useEffect(() => { setPage(1); }, [category, featured, q, listSize]);

  // Re-fetch whenever filters, view, page or size change.
  useEffect(() => { load(); },
    [category, featured, view, page, listSize, gridSize]);

  const submitSearch = (e) => {
    e?.preventDefault();
    const np = new URLSearchParams(params);
    if (q) np.set("q", q); else np.delete("q");
    setParams(np);
    setPage(1);
    load();
  };

  const setCategory = (c) => {
    const np = new URLSearchParams(params);
    if (c) np.set("category", c); else np.delete("category");
    setParams(np);
  };

  const hasMore = view === "grid" && products.length < total;
  const stat  = useMemo(() => products.filter(p => p.featured).length, [products]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-black dark:text-stone-100 transition-colors" data-testid="hustle-shop">
      <PlatformHeader
        brand={{ name: "HustleShop", to: "/hustleshop" }}
        showBookCta={false}
        showCart={false}
      />
      {/* ───────── Hero ───────── */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-rose-500/10 to-violet-500/10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-6 relative">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <img src={LOGO_LIGHT} alt="HustleShop"
                   className="w-20 h-20 sm:w-28 sm:h-28 object-contain drop-shadow"
                   data-testid="hustleshop-logo" />
              <div>
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
                  {t("hustleshop.title", { defaultValue: "HustleShop" })}
                </h1>
                <p className="text-slate-600 text-sm sm:text-base mt-1 max-w-md">
                  {t("hustleshop.tagline", { defaultValue: "Hand-picked gear from across the web — fishing, camping, marine, home, kitchen, electronics and more." })}
                </p>
              </div>
            </div>
            <Link to="/hustleplaza" className="text-sm font-semibold text-amber-700 hover:text-amber-900 underline">
              {t("hustleshop.back_to_plaza", { defaultValue: "← back to Hustle Plaza" })}
            </Link>
          </div>

          {/* Search */}
          <form onSubmit={submitSearch} className="mt-6 flex gap-2" data-testid="hustleshop-search-form">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("hustleshop.search_placeholder", { defaultValue: 'Search HustleShop — "Shimano reel", "air fryer", "tent"...' })}
              className="flex-1 bg-white border border-slate-300 rounded-full px-5 py-3 text-base focus:outline-none focus:border-amber-400 shadow-sm"
              data-testid="hustleshop-search-input"
            />
            <button type="submit"
                    className="bg-slate-900 text-white rounded-full px-6 py-3 font-semibold hover:bg-slate-700 transition shadow"
                    data-testid="hustleshop-search-submit">
              {t("hustleshop.search", { defaultValue: "Search" })}
            </button>
          </form>

          {/* Category chips */}
          <div className="mt-5 flex gap-2 overflow-x-auto pb-2 -mx-1" data-testid="hustleshop-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c.id || "all"}
                onClick={() => setCategory(c.id)}
                data-testid={`hustleshop-cat-${c.id || "all"}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
                  category === c.id
                    ? "bg-slate-900 text-white shadow"
                    : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400"
                }`}>
                <span aria-hidden>{c.emoji}</span>{" "}
                {t(`hustleshop.category.${c.id || "all"}`, { defaultValue: c.label })}
              </button>
            ))}
          </div>

          {/* Featured toggle */}
          <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span>
              {t("hustleshop.products_count", {
                defaultValue: "{{count}} products",
                count: total.toLocaleString("en-AU"),
              })}
              {category && ` — ${t(`hustleshop.category.${category}`, { defaultValue: CATEGORIES.find(c => c.id === category)?.label })}`}
            </span>
            <button
              onClick={() => {
                const np = new URLSearchParams(params);
                featured ? np.delete("featured") : np.set("featured", "1");
                setParams(np);
              }}
              data-testid="hustleshop-featured-toggle"
              className={`text-xs px-2.5 py-1 rounded-full ${featured ? "bg-amber-400 text-slate-900 font-semibold" : "bg-slate-100 hover:bg-slate-200"}`}>
              {featured
                ? t("hustleshop.featured_only", { defaultValue: "★ Featured only" })
                : t("hustleshop.show_featured", { defaultValue: "Show featured" })}
            </button>
            {isMarketplaceHost && (
              <MarketplaceViewToggle
                value={mpView}
                onChange={setMpView}
                testidPrefix="hustleshop-mp-view"
                className="ml-auto"
              />
            )}
          </div>
        </div>
      </header>

      {/* ───────── Grid ───────── */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {loading && (
          <div className="text-center py-16 text-slate-500" data-testid="hustleshop-loading">
            {t("hustleshop.loading", { defaultValue: "Loading shop…" })}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20 text-slate-500" data-testid="hustleshop-empty">
            {t("hustleshop.empty", { defaultValue: "No products match — try a different category or search term." })}
          </div>
        )}

        {!loading && products.length > 0 && (
          <>
            {/* Top pagination: view + page-size (top-right per user spec) */}
            <PlazaPagination
              view={view}
              onView={setView}
              pageSize={pageLimit}
              onPageSize={(n) => view === "list" ? setListSize(n) : setGridSize(n)}
              pageSizeOptions={view === "list" ? LIST_SIZES : GRID_SIZES}
              shown={products.length}
              total={total}
              countLabel={
                <>{t("hustleshop.showing_of", {
                  defaultValue: "Showing {{shown}} of {{total}}",
                  shown: products.length.toLocaleString("en-AU"),
                  total: total.toLocaleString("en-AU"),
                })}</>
              }
              page={page}
              totalPages={totalPages}
              onPage={setPage}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              labels={{
                show:       t("hustleshop.show",       { defaultValue: "Show" }),
                perPage:    t("hustleshop.per_page",   { defaultValue: "per page" }),
                listView:   t("hustleshop.list_view",  { defaultValue: "List" }),
                gridView:   t("hustleshop.grid_view",  { defaultValue: "Grid" }),
                loadMore:   t("hustleshop.load_more",  { defaultValue: "Load more" }),
                loading:    t("common.loading",        { defaultValue: "Loading…" }),
                firstTitle: t("hustleshop.first_page", { defaultValue: "First page" }),
                prevTitle:  t("hustleshop.prev_page",  { defaultValue: "Previous page" }),
                nextTitle:  t("hustleshop.next_page",  { defaultValue: "Next page" }),
                lastTitle:  t("hustleshop.last_page",  { defaultValue: "Last page" }),
                lastLabel:  t("hustleshop.last",       { defaultValue: "Last" }),
              }}
              testidPrefix="hustleshop-pagi"
            />

            {view === "grid" ? (
              <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mt-4"
                   data-testid="hustleshop-grid">
                {filterByMpView(products).map((p) => (
                  <article key={p.slug}
                           className="group bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition overflow-hidden flex flex-col"
                           data-testid={`product-${p.slug}`}>
                    <div className="relative aspect-square bg-slate-100 overflow-hidden">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.title}
                             className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                             loading="lazy"
                             onError={(e) => { e.target.style.display = "none"; }} />
                      ) : (
                        <div className="flex items-center justify-center h-full text-5xl opacity-30">🛍️</div>
                      )}
                      {p.featured && (
                        <span className="absolute top-2 left-2 bg-amber-400 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full">★ FEATURED</span>
                      )}
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="font-bold text-slate-900 leading-tight">{p.title}</h3>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.subtitle}</p>

                      {/* Price + rating row */}
                      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                        {p.price_display ? (
                          <>
                            <span className="text-lg font-extrabold text-slate-900" data-testid={`price-${p.slug}`}>
                              {p.price_display}
                            </span>
                            {p.price_currency && (
                              <span className="text-[10px] font-semibold text-slate-500 uppercase">{p.price_currency}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-slate-400 italic" data-testid={`price-${p.slug}`}>
                            {t("hustleshop.price_check", { defaultValue: "See price on Amazon" })}
                          </span>
                        )}
                        {p.rating && (
                          <span className="ml-auto text-xs text-amber-700 font-semibold" data-testid={`rating-${p.slug}`}>
                            ★ {typeof p.rating === "number" ? p.rating.toFixed(1) : p.rating}
                            {p.reviews_count ? (
                              <span className="text-slate-400 font-normal"> ({p.reviews_count.toLocaleString("en-AU")})</span>
                            ) : null}
                          </span>
                        )}
                      </div>
                      {/* iter256: MP vs Retail price + Save-Value chip.
                          Zero render when mp_price/retail_price are
                          missing or equal — Retail-only catalogues
                          stay pixel-identical. */}
                      {Number(p.mp_price) > 0 && Number(p.retail_price) > 0 && Number(p.mp_price) < Number(p.retail_price) && (
                        <div className="mt-2 flex items-baseline gap-2 flex-wrap"
                             data-testid={`mp-row-${p.slug}`}>
                          <span className="text-[11px] uppercase tracking-widest font-black text-emerald-700">MP</span>
                          <span className="text-base font-black text-emerald-700"
                                data-testid={`mp-price-${p.slug}`}>${Number(p.mp_price).toFixed(2)}</span>
                          <span className="text-[11px] text-slate-400 line-through"
                                data-testid={`retail-price-${p.slug}`}>${Number(p.retail_price).toFixed(2)}</span>
                          <SaveValueChip
                            retail={p.retail_price}
                            mp={p.mp_price}
                            testid={`save-chip-${p.slug}`}
                          />
                        </div>
                      )}

                      <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">{p.subcategory?.replace(/-/g, " ")}</span>
                        <a href={p.amazon_url} target="_blank" rel="sponsored noopener noreferrer"
                           className="text-xs font-bold bg-slate-900 hover:bg-amber-500 hover:text-slate-900 text-white px-3 py-2 rounded transition"
                           data-testid={`buy-${p.slug}`}>
                          {t("hustleshop.buy", { defaultValue: "Buy →" })}
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white"
                   data-testid="hustleshop-list">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-2 w-16">{t("hustleshop.col_image",    { defaultValue: "Image" })}</th>
                      <th className="text-left px-3 py-2">        {t("hustleshop.col_title",    { defaultValue: "Title" })}</th>
                      <th className="text-left px-3 py-2">        {t("hustleshop.col_category", { defaultValue: "Category" })}</th>
                      <th className="text-right px-3 py-2 w-24">  {t("hustleshop.col_price",    { defaultValue: "Retail" })}</th>
                      <th className="text-right px-3 py-2 w-24">  {t("hustleshop.col_mp_price", { defaultValue: "MP" })}</th>
                      <th className="text-right px-3 py-2 w-24">  {t("hustleshop.col_savings",  { defaultValue: "Save" })}</th>
                      <th className="text-left px-3 py-2 w-20">   {t("hustleshop.col_rating",   { defaultValue: "Rating" })}</th>
                      <th className="text-right px-3 py-2 w-24">  {t("hustleshop.col_action",   { defaultValue: "Action" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterByMpView(products).map((p) => (
                      <tr key={p.slug}
                          className="border-t border-slate-100 hover:bg-amber-50/40"
                          data-testid={`hustleshop-row-${p.slug}`}>
                        <td className="px-3 py-2">
                          {p.image_url ? (
                            <img src={p.image_url} alt=""
                                 className="w-12 h-12 object-cover rounded"
                                 loading="lazy"
                                 onError={(e) => { e.target.style.display = "none"; }} />
                          ) : (
                            <div className="w-12 h-12 rounded bg-slate-100" />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-900 leading-snug">{p.title}</div>
                          <div className="text-xs text-slate-500 line-clamp-1">{p.subtitle}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{p.subcategory?.replace(/-/g, " ")}</td>
                        <td className="px-3 py-2 text-right font-bold text-slate-900" data-testid={`hustleshop-price-${p.slug}`}>
                          {Number(p.retail_price) > 0
                            ? <>${Number(p.retail_price).toFixed(2)}</>
                            : (p.price_display || <span className="text-slate-400 font-normal italic">—</span>)}
                        </td>
                        <td className="px-3 py-2 text-right font-black text-emerald-700"
                            data-testid={`hustleshop-mp-price-${p.slug}`}>
                          {Number(p.mp_price) > 0 ? <>${Number(p.mp_price).toFixed(2)}</> : <span className="text-slate-300 font-normal">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right"
                            data-testid={`hustleshop-savings-${p.slug}`}>
                          <SaveValueChip retail={p.retail_price} mp={p.mp_price} testid={`hustleshop-savechip-${p.slug}`} />
                        </td>
                        <td className="px-3 py-2 text-xs" data-testid={`hustleshop-rating-${p.slug}`}>
                          {p.rating ? (
                            <span className="text-amber-700 font-semibold">
                              ★ {typeof p.rating === "number" ? p.rating.toFixed(1) : p.rating}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <a href={p.amazon_url}
                             target="_blank"
                             rel="sponsored noopener noreferrer"
                             className="inline-block text-xs font-bold bg-slate-900 hover:bg-amber-500 hover:text-slate-900 text-white px-3 py-1.5 rounded transition"
                             data-testid={`hustleshop-buy-${p.slug}`}>
                            {t("hustleshop.buy", { defaultValue: "Buy →" })}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Bottom pagination: numbered nav (list) or load-more (grid) */}
            <PlazaPagination
              view={view}
              onView={setView}
              showViewToggle={false}
              pageSize={pageLimit}
              onPageSize={(n) => view === "list" ? setListSize(n) : setGridSize(n)}
              pageSizeOptions={view === "list" ? LIST_SIZES : GRID_SIZES}
              shown={products.length}
              total={total}
              countLabel={
                <>{t("hustleshop.showing_of", {
                  defaultValue: "Showing {{shown}} of {{total}}",
                  shown: products.length.toLocaleString("en-AU"),
                  total: total.toLocaleString("en-AU"),
                })}</>
              }
              page={page}
              totalPages={totalPages}
              onPage={setPage}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              labels={{
                show:       t("hustleshop.show",       { defaultValue: "Show" }),
                perPage:    t("hustleshop.per_page",   { defaultValue: "per page" }),
                listView:   t("hustleshop.list_view",  { defaultValue: "List" }),
                gridView:   t("hustleshop.grid_view",  { defaultValue: "Grid" }),
                loadMore:   t("hustleshop.load_more",  { defaultValue: "Load more" }),
                loading:    t("common.loading",        { defaultValue: "Loading…" }),
                firstTitle: t("hustleshop.first_page", { defaultValue: "First page" }),
                prevTitle:  t("hustleshop.prev_page",  { defaultValue: "Previous page" }),
                nextTitle:  t("hustleshop.next_page",  { defaultValue: "Next page" }),
                lastTitle:  t("hustleshop.last_page",  { defaultValue: "Last page" }),
                lastLabel:  t("hustleshop.last",       { defaultValue: "Last" }),
              }}
              testidPrefix="hustleshop-pagi-bottom"
            />
          </>
        )}
      </main>

      {/* ───────── Affiliate disclosure ───────── */}
      <footer className="bg-slate-100 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-xs text-slate-600">
          <div className="flex items-center gap-3 mb-3">
            <img src={LOGO_DARK} alt="" className="w-12 h-12 object-contain opacity-90" />
            <p className="font-bold text-slate-700 text-base">
              {t("hustleshop.affiliate_title", { defaultValue: "HustleShop affiliate disclosure" })}
            </p>
          </div>
          <p>
            {t("hustleshop.affiliate_body", { defaultValue: "HustleShop is operated by Hustle Plaza as a curated affiliate storefront. We partner with Amazon Associates Australia and earn a small commission when you buy through our links — at no extra cost to you. Prices, availability and shipping are set by the seller (Amazon Australia or its merchants). Hustle Plaza does not stock, ship or warrant these products directly." })}
          </p>
          <p className="mt-2">
            {t("hustleshop.tracking_id", { defaultValue: "Tracking ID:" })}{" "}
            <code className="bg-slate-200 px-1.5 py-0.5 rounded">hustle0e-22</code> ·{" "}
            {t("hustleshop.region", { defaultValue: "Region: Australia" })}
          </p>
        </div>
      </footer>
      <SuiteFooter />
    </div>
  );
}
